/**
 * PATH: apps/hr/src/pages/employees/employee-detail/useEmployeeDetail.ts
 * PURPOSE: Data fetching, document mapping, save handlers for employee detail
 */

import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { hrApi } from '../../../lib/setupApi'
import type { Employee, EmployeeStatus, Document, LeaveRequest, PayslipLine } from '../../../types'
import { type WorkerDetailApiResponse, mapWorkerDetailToEmployee, mapTicketTypeToDocumentType, EMPLOYMENT_TYPE_TO_API } from './employee-api-types'

export function useEmployeeDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Modal states
  const [activeTab, setActiveTab] = useState('overview')
  const [editEmploymentOpen, setEditEmploymentOpen] = useState(false)
  const [editContactOpen, setEditContactOpen] = useState(false)
  const [editEmergencyOpen, setEditEmergencyOpen] = useState(false)
  const [editBankOpen, setEditBankOpen] = useState(false)
  const [editPersonalOpen, setEditPersonalOpen] = useState(false)
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false)
  const [confirmBlockOpen, setConfirmBlockOpen] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [localUpdates, setLocalUpdates] = useState<Partial<Employee>>({})
  const [isSaving, setIsSaving] = useState(false)

  // Data fetching
  const isUuidFormat = id ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) : false
  const isNumericId = id ? /^\d+$/.test(id) : false

  const { data: apiEmployee, isLoading: apiLoading, error: apiError, refetch } = useQuery({
    queryKey: ['hr-employee', id],
    queryFn: async () => {
      if (!id) return null
      if (isUuidFormat) { const r = await hrApi.getEmployee(id); return r.data as WorkerDetailApiResponse }
      if (isNumericId) {
        const listResponse = await hrApi.getEmployees()
        const match = (listResponse.data?.workers || []).find((w: WorkerDetailApiResponse) => w.simpro_id === parseInt(id, 10))
        if (match) { const r = await hrApi.getEmployee(match.id); return r.data as WorkerDetailApiResponse }
        throw new Error(`No employee found with SimPro ID ${id}`)
      }
      const r = await hrApi.getEmployee(id); return r.data as WorkerDetailApiResponse
    },
    enabled: !!id, staleTime: 30_000, retry: 1,
  })

  const employee = useMemo(() => apiEmployee ? { ...mapWorkerDetailToEmployee(apiEmployee), ...localUpdates } : null, [apiEmployee, localUpdates])

  // Documents
  const { data: documentsData, refetch: refetchDocuments } = useQuery({
    queryKey: ['employee-documents', id],
    queryFn: async () => { if (!id) return []; try { return (await hrApi.getEmployeeDocuments(id)).data } catch { return [] } },
    enabled: !!id,
  })

  const documents: Document[] = useMemo(() => {
    if (!documentsData || !employee) return []
    return documentsData.map((doc: any) => ({
      id: doc.id, documentNumber: doc.ticket_number || '', documentType: mapTicketTypeToDocumentType(doc.ticket_type),
      documentName: doc.document_name || doc.ticket_type, employeeId: id, employeeName: `${employee.firstName} ${employee.lastName}`,
      status: (doc.document_status as Document['status']) || 'pending_ocr', issueDate: doc.issued_date, expiryDate: doc.expiry_date,
      daysUntilExpiry: doc.days_until_expiry, confidenceScore: doc.ocr_confidence ? Math.round(doc.ocr_confidence * 100) : undefined,
      extractedFields: doc.ocr_extracted_fields?.additionalFields ? Object.entries(doc.ocr_extracted_fields.additionalFields).map(([name, value]) => ({ name, value: String(value), confidence: doc.ocr_confidence || 0, isEdited: false })) : undefined,
      tags: doc.tags || doc.ocr_extracted_fields?.suggestedTags || [], fileUrl: doc.file_url || '', fileName: doc.file_name || '',
      fileType: doc.file_type || '', fileSize: doc.file_size, issuer: doc.issuer, notes: doc.notes,
      createdAt: doc.created_at || '', updatedAt: doc.updated_at || '',
    }))
  }, [documentsData, employee, id])

  // Placeholders
  const leaveRequests: LeaveRequest[] = []
  const payslips: PayslipLine[] = []

  // Save helpers
  const saveAndRefetch = async (fn: () => Promise<void>, revertKeys: string[]) => {
    if (!id) return; setIsSaving(true)
    try { await fn(); await refetch() }
    catch (err) { console.error('[HR] Save failed:', err); setLocalUpdates(prev => { const next = { ...prev }; revertKeys.forEach(k => delete (next as any)[k]); return next }) }
    finally { setIsSaving(false) }
  }

  const handleSaveEmployment = (data: Partial<Employee>) => {
    setLocalUpdates(prev => ({ ...prev, ...data }))
    return saveAndRefetch(async () => {
      await hrApi.updateEmployee(id!, { department: data.department, position: data.position, employment_type: data.employmentType ? EMPLOYMENT_TYPE_TO_API[data.employmentType] : undefined, hire_date: data.hireDate, location: data.location, manager_id: data.managerId })
      setEditEmploymentOpen(false)
    }, ['department', 'position', 'employmentType', 'hireDate', 'location', 'managerName'])
  }

  const handleSaveContact = (data: Partial<Employee>) => {
    setLocalUpdates(prev => ({ ...prev, ...data }))
    return saveAndRefetch(async () => {
      await hrApi.updateEmployee(id!, { email: data.email, mobile: data.phone, address_line_1: data.address?.line1, address_line_2: data.address?.line2, city: data.address?.city, state: data.address?.state, postcode: data.address?.postalCode, country: data.address?.country })
      setEditContactOpen(false)
    }, ['email', 'phone', 'address'])
  }

  const handleSaveEmergency = (data: Partial<Employee>) => {
    setLocalUpdates(prev => ({ ...prev, ...data }))
    return saveAndRefetch(async () => {
      if (data.emergencyContact) await hrApi.createEmployeeEmergencyContact(id!, { contact_name: data.emergencyContact.name, relationship: data.emergencyContact.relationship, phone_primary: data.emergencyContact.phone, email: data.emergencyContact.email, is_primary: true })
      setEditEmergencyOpen(false)
    }, ['emergencyContact'])
  }

  const handleSaveBank = (data: Partial<Employee>) => {
    setLocalUpdates(prev => ({ ...prev, ...data }))
    return saveAndRefetch(async () => {
      if (data.bankDetails) await hrApi.createEmployeeBankDetails(id!, { account_name: data.bankDetails.accountName, bsb: data.bankDetails.bsb, account_number: data.bankDetails.accountNumber, bank_name: data.bankDetails.bankName, is_primary: true })
      setEditBankOpen(false)
    }, ['bankDetails'])
  }

  const handleSavePersonal = (data: Partial<Employee>) => {
    setLocalUpdates(prev => ({ ...prev, ...data }))
    return saveAndRefetch(async () => {
      await hrApi.updateEmployee(id!, { date_of_birth: data.dateOfBirth, gender: data.gender })
      setEditPersonalOpen(false)
    }, ['dateOfBirth', 'gender'])
  }

  const handleBlockUser = async () => {
    if (!employee) return; setIsSaving(true)
    try {
      const newStatus = employee.status === 'inactive' ? 'active' : 'inactive'
      setLocalUpdates(prev => ({ ...prev, status: newStatus as EmployeeStatus }))
      await new Promise(resolve => setTimeout(resolve, 500))
      setConfirmBlockOpen(false)
    } catch (err) { console.error('[HR] Block failed:', err) }
    finally { setIsSaving(false) }
  }

  const handleDeleteEmployee = async () => {
    setIsSaving(true)
    try { await new Promise(resolve => setTimeout(resolve, 500)); setConfirmDeleteOpen(false); navigate('/employees') }
    catch (err) { console.error('[HR] Delete failed:', err) }
    finally { setIsSaving(false) }
  }

  return {
    id, navigate, employee, apiLoading, apiError, refetch, documents, refetchDocuments,
    leaveRequests, payslips, activeTab, setActiveTab,
    editEmploymentOpen, setEditEmploymentOpen, editContactOpen, setEditContactOpen,
    editEmergencyOpen, setEditEmergencyOpen, editBankOpen, setEditBankOpen,
    editPersonalOpen, setEditPersonalOpen, actionsMenuOpen, setActionsMenuOpen,
    confirmBlockOpen, setConfirmBlockOpen, confirmDeleteOpen, setConfirmDeleteOpen,
    isSaving, handleSaveEmployment, handleSaveContact, handleSaveEmergency,
    handleSaveBank, handleSavePersonal, handleBlockUser, handleDeleteEmployee,
  }
}
